import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Form, Row, Spin } from 'antd';

import { ENV_VARS } from '@/env';
import { getAllUsersNameEffect } from '@/redux/RoutingGuide/RoutingGuideEffects';
import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';

import { addToCurrentQuoteCollaborators, setQuoteModalBlocker } from '../../../../../redux/common/CommonActions';
import Button from '../../../../components/Buttons/Button';
import FormCheckBox from '../../../../components/Inputs/FormCheckBox';
import FormInput from '../../../../components/Inputs/FormInput';
import HalCollboratorLogDetails from '../../../HalRoutingGuide/Component/AddRoutingGuide/tabs/HalCollboratorLogDetails';
import { mapCollaboratorsSearchResult } from '../../utils';

const CollaboratorsModal = ({ collaborators, handleClose }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [searchForm] = Form.useForm();
  const [searchResultsForm] = Form.useForm();
  const formSearchResultCollaborators = Form.useWatch('collaboratorsList', searchResultsForm);

  const [searchableCollaborators, setSearchableCollaborators] = useState([]);
  const existedCollaborators = collaborators?.map((c) => c.userId) || [];

  const isCheckedAllCollaborators = formSearchResultCollaborators?.every((i) => i.isChecked);
  const quoteModalBlocker = useSelector((state) => state.common.quoteModalBlocker);

  const handleCheckAllCollaborators = () => {
    const newValues = formSearchResultCollaborators.map((i) => ({
      ...i,
      isChecked: !isCheckedAllCollaborators,
    }));
    searchResultsForm.setFieldValue('collaboratorsList', newValues);
  };

  const filterCollaborators = (data) => {
    const userNameValue = searchForm.getFieldValue('UserName');
    const emailValue = searchForm.getFieldValue('Email');
    if (!userNameValue && !emailValue) {
      return data;
    }

    return data.filter((user) => {
      const emailSearch = emailValue?.toLowerCase();
      const nameSearch = userNameValue?.toLowerCase();

      const targetName = user.userName?.toLowerCase();
      const targetEmail = user.emails?.toLowerCase();

      if (nameSearch && emailSearch) {
        return targetName.includes(nameSearch) && targetEmail.includes(emailSearch);
      }

      if (nameSearch) {
        return targetName.includes(nameSearch);
      }
      if (emailSearch) {
        return targetEmail.includes(emailSearch);
      }
      return false;
    });
  };

  const handleSearchCollaborators = async () => {
    setIsLoading(true);
    try {
      // Investigate what params we support, make filter on the API side
      const { data } = await getAllUsersNameEffect({});
      const allCollaborators = data.reduce(mapCollaboratorsSearchResult, []);
      const filteredCollaborators = filterCollaborators(allCollaborators);
      setSearchableCollaborators(filteredCollaborators);
      searchResultsForm.setFieldsValue({
        collaboratorsList: filteredCollaborators,
      });
      setIsLoading(false);
    } catch {
      showNotification({
        message: 'Something went wrong',
        type: NOTIFICATION_TYPES.ERROR,
      });
      setIsLoading(false);
    }
  };

  const handleCloseCollaboratorModal = () => {
    handleClose();
    searchForm.resetFields();
    searchResultsForm.resetFields();
    setSearchableCollaborators([]);
  };

  const handleAddCollaborator = () => {
    const checkedNewCollaborators = formSearchResultCollaborators?.filter(
      (c) => c.isChecked && !existedCollaborators.includes(c?.userId),
    );

    dispatch(addToCurrentQuoteCollaborators(checkedNewCollaborators));

    if (!quoteModalBlocker) {
      dispatch(setQuoteModalBlocker(true));
    }

    handleCloseCollaboratorModal();
  };

  const handleClearSearchForm = () => {
    searchForm.resetFields();
  };

  const handleClearSearchResults = () => {
    searchForm.resetFields();
    searchResultsForm.resetFields();
    setSearchableCollaborators([]);
  };

  const isHBUser = ENV_VARS.REACT_APP_THEME === 'halliburton';

  return (
    <Col className="px-30 w-100 pt-15 flex-1 h-100 custom-scroll-bar" justify="space-between" gutter={16} align="start">
      <Col>
        <p className="font-size-14">1. Search for users by filling out the required parameters</p>
      </Col>
      <Col>
        <Form form={searchForm} name="CollaboratorsModalSearchForm" className="pt-15">
          <Row gutter={30}>
            <Col sm={8}>
              <FormInput name="UserName" label="User Name" margin="m-0" editMode />
            </Col>
          </Row>

          <Row gutter={30} className="mt-2">
            <Col sm={8}>
              <FormInput name="Email" label="Email" margin="m-0" editMode />
            </Col>
            <Row justify="end" align="middle" className="pt-18">
              <Col className="px-15 py-5">
                <Row gutter={15}>
                  <Col>
                    <Button
                      dataTestId="collaborators-modal-search-button"
                      onClick={handleSearchCollaborators}
                      className={isHBUser ? 'hb-button' : ''}
                    >
                      Search
                    </Button>
                  </Col>

                  <Col>
                    <Button
                      type="default"
                      dataTestId="collaborators-modal-clear-button"
                      className={isHBUser ? 'hb-button' : ''}
                      onClick={handleClearSearchForm}
                    >
                      Clear
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Row>
        </Form>
      </Col>
      <Col className="mt-40">
        <p className="font-size-14">2. Select users to add them to collaborators</p>
      </Col>
      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center mt-50">
          <Spin />
        </div>
      ) : searchableCollaborators.length ? (
        <Form
          name="searchResultsProviders"
          form={searchResultsForm}
          initialValues={{ collaboratorsList: searchableCollaborators }}
          onFinish={handleAddCollaborator}
        >
          <FormCheckBox
            checkBoxClass="normal-checkbox"
            label={`All users (${searchableCollaborators.length})`}
            className="pl-22 pt-20 m-0"
            onChange={handleCheckAllCollaborators}
          />
          <Col className="d-flex flex-direction-column pt-10">
            <div className="custom-scroll-bar">
              <Form.List name="collaboratorsList">
                {(collaboratorsList) => (
                  <div>
                    {collaboratorsList.map(({ name }, index) => (
                      <HalCollboratorLogDetails
                        key={name}
                        name={name}
                        index={index}
                        collaborator={formSearchResultCollaborators?.[name]}
                        handleShowModal={() => {}}
                        isHideButtons
                        existedCollaborators={existedCollaborators}
                        showType={false}
                      />
                    ))}
                  </div>
                )}
              </Form.List>
            </div>
            <Row justify="end" align="middle" className="mt-20">
              <Col className="px-15 py-5">
                <Row gutter={15}>
                  <Col>
                    <Button
                      onClick={handleAddCollaborator}
                      dataTestId="collaborators-modal-add-users-button"
                      className={isHBUser ? 'hb-button' : ''}
                    >
                      Add users
                    </Button>
                  </Col>

                  <Col>
                    <Button
                      type="default"
                      variant="danger"
                      dataTestId="collaborators-modal-cancel-button"
                      className={isHBUser ? 'hb-button' : ''}
                      onClick={handleClearSearchResults}
                    >
                      Clear Users
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
        </Form>
      ) : (
        <Row className="py-30 my-15 text-center background-grey">
          <p>No search results found</p>
        </Row>
      )}
    </Col>
  );
};

export default CollaboratorsModal;
